import { useQuery } from '@tanstack/react-query'
import { memberService } from '@/lib/api/services/member'
import type {
  ApiResponse,
  PaginatedResponse,
  PaginationParams,
  Member,
  MemberDetail,
  MemberFilterParams
} from '@/lib/api/types'

// Query keys for caching
export const memberKeys = {
  all: ['members'] as const,
  lists: () => [...memberKeys.all, 'list'] as const,
  list: (params: PaginationParams, filters: MemberFilterParams) =>
    [...memberKeys.lists(), params, filters] as const,
  details: () => [...memberKeys.all, 'detail'] as const,
  detail: (id: string) => [...memberKeys.details(), id] as const
}

/**
 * Hook to fetch members with pagination and filters
 */
export function useFilterMembers(
  params: PaginationParams = { page: 1, size: 10 },
  filters: MemberFilterParams = {}
) {
  return useQuery<ApiResponse<PaginatedResponse<Member>>, Error>({
    queryKey: memberKeys.list(params, filters),
    queryFn: () => memberService.getMembers(params, filters),
    staleTime: 5 * 60 * 1000 // 5 minutes
  })
}

/**
 * Hook to fetch a single member by ID
 */
export function useMember(memberId?: string) {
  return useQuery<ApiResponse<MemberDetail>, Error>({
    queryKey: memberKeys.detail(memberId || ''),
    queryFn: () => memberService.getMemberById(memberId!),
    enabled: !!memberId,
    staleTime: 5 * 60 * 1000 // 5 minutes
  })
}

